import { PluploadUploader } from './lib/Uploader.js';
import { PluploadFile } from './lib/File.js';
import constants from './lib/constants.js';
import ensure from '../../../libs/ensure/ensure.js';

/**
 * The plupload namespace
 * @type {Object}
 */
export const plupload = {
	// compatible with this plupload version
	VERSION: '2.3.6',
	/**
	 * Class to create a new upload widget
	 */
	Uploader: PluploadUploader,
	/**
	 * A wrapper for native File objects
	 */
	File: PluploadFile,
	// all our constants
	...constants,
	/**
	 * Register a filter that can be specified to allow or disallow a file from being added
	 * @param {String} filterName  The name of the filter
	 * @param {Function} filterFunction  A function that receives the configuration value,
	 *   the File object and a callback to invoke with true if the filter allows the file
	 */
	addFileFilter: PluploadUploader.addFileFilter,
	/**
	 * Return which runtime this browser will use (always html5)
	 * @returns {string}
	 */
	predictRuntime: () => 'html5',
	/**
	 * Cleans the specified name from national characters (diacritics). The result will be a name with only a-z, 0-9, _ and .
	 * @param {String} name String to clean up.
	 * @return {String} Cleaned string.
	 */
	cleanName: PluploadFile.cleanName,
	/**
	 * Format bytes into human-readable format
	 * @param {Number} bytes
	 * @param {Number} [precision=0]  The number of decimal places to use
	 * @returns {String}
	 */
	formatSize: (bytes, precision = 0) => {
		const suffixes = ['b', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
		let power = 0;
		while (bytes >= 1000) {
			bytes /= 1024;
			power++;
		}
		if (power === 0) {
			precision = 0;
		}
		if (!Number.isFinite(bytes)) {
			bytes = 0;
		}
		return bytes.toFixed(precision) + ' ' + suffixes[power];
	},
	/**
	 * Convert a byte count such as "5MB" into a number (1024*1024*5)
	 * @param {String|Number} size
	 * @returns {number}
	 */
	parseSize: size => {
		const powers = ['b', 'kb', 'mb', 'gb', 'tb', 'pb', 'eb', 'zb', 'yb'];
		const match = String(size).match(/^(\d+)\s*(|b|kb|mb|gb|tb|pb|eb|zb|yb)$/i);
		if (match) {
			let bytes = parseFloat(match[1]);
			const power = powers.indexOf(match[2].toLowerCase());
			if (power > 0) {
				bytes *= Math.pow(1024, power);
			}
			return bytes;
		}
		return 0;
	},
};

/**
 * Filter for file size. Accepts a number of bytes or a number with a unit such as "2GB" or "250 mb"
 */
plupload.addFileFilter('max_file_size', function (size, file, cb) {
	const bytes = plupload.parseSize(size);
	const allowed = !bytes || file.size <= bytes;
	if (!allowed) {
		this.trigger('Error', {
			code: plupload.FILE_SIZE_ERROR,
			message: 'File too large.',
			file: file,
		});
	}
	cb(allowed);
});

/**
 * Prevents a file from being added more than once
 */
plupload.addFileFilter('prevent_duplicates', function (shouldPrevent, file, cb) {
	const allowed =
		!shouldPrevent ||
		this.files.every(f => {
			return f.name + f.size !== file.name + file.size;
		});
	if (!allowed) {
		this.trigger('Error', {
			code: plupload.FILE_DUPLICATE_ERROR,
			message: 'File already added.',
			file: file,
		});
	}
	cb(allowed);
});

/**
 * Prevents empty files from being added
 */
plupload.addFileFilter('prevent_empty', function (preventEmpty, file, cb) {
	const allowed = !preventEmpty || file.size === 0;
	if (!allowed) {
		this.trigger('Error', {
			code: plupload.FILE_SIZE_ERROR,
			message: 'File is empty',
			file: file,
		});
	}
	cb(allowed);
});

plupload.addFileFilter('allowed_extensions', function (extensionList, file, addFile) {
	const extension = file.name.indexOf('.') > -1 ? ensure.string(file.name).split('.').pop().toLowerCase() : '';
	const allowed = extensionList === '*' || extensionList.includes(extension);

	if (!allowed) {
		this.trigger('Error', {
			code: plupload.FILE_EXTENSION_ERROR,
			message: `The file ${file.name} is not supported by the platform. Please contact us if you need to support it.`,
			file: file,
		});
	}

	addFile(allowed);
});
